import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Flex, Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

//components
import ComponentHeader from 'components/componentHeader/componentHeader';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import VerificationModal from 'modules/main/actions/verificationModal';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import NotificationService from 'core/services/notification';
import MemberForm from '../components/memberForm';
import Icon from 'utils/icon';

// services
import { mapResponseErrors } from 'utils/mapResponseErrors';

//models
import { VerificationPayload } from 'modules/main/actions/commonModels';
import { CommitteeMember } from '../models/members';
import { isRTL } from 'core/services/language';
import { Committee } from '../models/committee';
import { onAppointCommittee, onGetCommitteeById } from '../services/committeeServices';
import { onAddMemberToCommittee, onDeleteMemberFromCommittee, onEditMemberFromCommittee, onGetCommitteeMembers } from '../services/memberServices';
import CommitteeMembers from '../components/committeeMembers';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface BoardAttachMembersPageProps {}

const CommitteeAttachMembersPage: FunctionComponent<BoardAttachMembersPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [addForm] = Form.useForm();
	const [editForm] = Form.useForm();

	const [committee, setCommittee] = useState<Committee>();
	const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([]);

	const [tempEditedMemberId, setTempEditedMemberId] = useState<number | null>(null);
	const [tempDeletedMemberId, setTempDeletedMemberId] = useState<number | null>(null);

	const [addModalOpen, setAddModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState(false);

	const [isLoadingBoard, setIsLoadingBoard] = useState(true);
	const [isLoadingMembers, setIsLoadingMembers] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAppointing, setIsAppointing] = useState(false);

	useEffect(() => {
		getCommitteeDetails();
		loadMembersHandler();
	}, []);

	const getCommitteeDetails = async () => {
		try {
			setIsLoadingBoard(true);

			const response = await onGetCommitteeById(id!);
			setCommittee(response);

			setIsLoadingBoard(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.FETCH_COMMITTEE_DETAILS_ERROR'));
			navigate('/dashboard/actions/boards/committees');
		}
	};

	const loadMembersHandler = async () => {
		try {
			setIsLoadingMembers(true);

			const response = await onGetCommitteeMembers(id!, 1);

			setCommitteeMembers(response.members);

			setIsLoadingMembers(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoadingMembers(false);
		}
	};

	const addHandler = async (member: CommitteeMember,isNewMember: boolean) => {
		try {
			setIsAdding(true);

			const payload = new FormData();

			if (!isNewMember) {
				payload.append('national_id', member.national_id);
				payload.append('position', member.position);
				payload.append('type', member.type);
			} else {
				Object.entries(member).forEach(([key, value]) => {
					payload.append(key, value as any);
				});
			}

			await onAddMemberToCommittee(id!, payload);

			loadMembersHandler();
			addForm.resetFields();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMITTEE_MEMBER_CREATED_SUCCESSFULLY'));

			setIsAdding(false);
			setAddModalOpen(false);
		} catch (error: any) {
			setIsAdding(false);

			if (error.status === 422) {
				mapResponseErrors(error, addForm);
				return;
			}

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const editHandler = async (member: CommitteeMember, isNewMember: boolean) => {
		try {
			setIsEditing(true);

			const payload = new FormData();
			payload.append('_method', 'PUT');

			if (!isNewMember) {
				payload.append('national_id', member.national_id);
				payload.append('position', member.position);
				payload.append('type', member.type);
			} else {
				Object.entries(member).forEach(([key, value]) => {
					payload.append(key, value as any);
				});
			}

			await onEditMemberFromCommittee(id!, tempEditedMemberId!, payload);

			loadMembersHandler();

			editForm.resetFields();
			setIsEditing(false);
			setEditModalOpen(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMITTEE_MEMBER_UPDATED_SUCCESSFULLY'));
		} catch (error: any) {
			setIsEditing(false);

			if (error.status === 422) {
				mapResponseErrors(error, editForm);
				return;
			}

			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const deleteHandler = async () => {
		try {
			setIsDeleting(true);

			await onDeleteMemberFromCommittee(id!, tempDeletedMemberId!);
			loadMembersHandler();

			setIsDeleting(false);

			setDeleteModalOpen(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMITTEE_MEMBER_DELETED_SUCCESSFULLY'));
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onAppoint = async ({ key, method }: VerificationPayload) => {
		try {
			setIsAppointing(true);

			await onAppointCommittee(id!, method, key);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMITTEE_APPOINTED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards/committees');
		} catch (error: any) {
			setIsAppointing(false);

			const errorMessage = error?.response?.data?.data?.governance_errors;
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : (error?.response?.data?.message ?? t('COMMON.SUBMIT_ERROR')));
		}
	};

	return (
		<>
			{isLoadingBoard ? (
				<LoadingComponent />
			) : (
				<Card>
					<Flex justify="space-between" align="flex-start" gap={30} wrap className="mb-4">
						<ComponentHeader title={t('ACTIONS.NEW_COMMITTEE_FORMATION')} subtitle={t('ACTIONS.FILL_TO_APPOINT_COMMITTEE')} />

						<Icon name="registrationStep2" width={280} height={50} style={{ transform: isRTL() ? 'rotateY(180deg)' : 'none' }} />
					</Flex>

					<Flex justify="space-between">
						<div>
							<h6 className="mb-0 fw-medium">
								{isRTL() ? committee?.name.ar : committee?.name.en} {t('ACTIONS.COMMITTEE_MEMBERS')}
							</h6>
							<p>{t('ACTIONS.ADD_COMMITTEE_MEMBERS')}</p>
						</div>

						{committee?.status && committee.status !== 'active' && (
							<Button type="primary" size="middle" onClick={() => setAddModalOpen(true)}>
								{t('ACTIONS.ADD_MEMBER')}
							</Button>
						)}
					</Flex>

					<CommitteeMembers
						committeeStatus={committee?.status!}
						members={committeeMembers || []}
						onEdit={(member) => {
							editForm.setFieldsValue(member);
							setTempEditedMemberId(member.id);
							setEditModalOpen(true);
						}}
						onDelete={(memberId) => {
							setTempDeletedMemberId(memberId);
							setDeleteModalOpen(true);
						}}
						isLoading={isLoadingMembers}
					/>

					{committee?.status && committee.status !== 'active' && (
						<Flex gap={20} justify="flex-end" className="mt-5">
							<Button type="primary" onClick={() => setVerificationModalOpen(true)}>
								{t('ACTIONS.APPOINT_COMMITTEE')}
							</Button>
						</Flex>
					)}
				</Card>
			)}

			{/* ADD MODAL */}
			<Modal
				open={addModalOpen}
				destroyOnClose
				width={900}
				title={t('ACTIONS.ADD_NEW_MEMBER')}
				onCancel={() => {
					setAddModalOpen(false);
					addForm.resetFields();
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setAddModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<Button key="confirm" type="primary" size="middle" onClick={addForm.submit} loading={isAdding}>
						{t('COMMON.ADD')}
					</Button>,
				]}>
				<MemberForm form={addForm} onSubmit={addHandler} />
			</Modal>

			{/* EDIT MODAL */}
			<Modal
				open={editModalOpen}
				destroyOnClose
				width={900}
				title={t('ACTIONS.EDIT_MEMBER')}
				onCancel={() => {
					setEditModalOpen(false);
					editForm.resetFields();
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setEditModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<SuspendedTooltip key="confirm">
						<Button key="confirm" type="primary" size="middle" onClick={editForm.submit} loading={isEditing}>
							{t('COMMON.UPDATE')}
						</Button>
					</SuspendedTooltip>,
				]}>
				<MemberForm form={editForm} onSubmit={editHandler} />
			</Modal>

			{/* DELETE MODAL */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				title={t('ACTIONS.DELETE_MEMBER')}
				subTitle={t('ACTIONS.CONFIRM_DELETE_MEMBER')}
				confirmText={t('COMMON.DELETE')}
				noteType="red"
				note={t('ACTIONS.COMMITTEE_MEMBER_DELETE_NOTE')}
				confirmColor="red"
				onDiscard={() => setDeleteModalOpen(false)}
				onConfirm={deleteHandler}
				isLoading={isDeleting}
			/>

			{/* VERIFICATION MODAL */}
			<VerificationModal
				open={verificationModalOpen}
				isLoading={isAppointing}
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onAppoint}
				note={t('ACTIONS.COMMITTEE_APPOINTMENT_VERIFICATION_NOTE')}
				noteType="red"
				noteStyles={{ marginTop: -20, marginBottom: 20 }}
				module="board"
				id={id!}
			/>
		</>
	);
};

export default CommitteeAttachMembersPage;
